import { useCallback, useState } from 'react';

import { Box, Button, Dialog, H2, Icon, RadioGroup, Text, VisuallyHidden } from '@betterment/samba-react';
import { ThemedImage } from '@betterment/shared-components/src/components/themed-image/ThemedImage';
import { min } from 'es-toolkit/compat';
import { useFormikContext } from 'formik';
import ReactCrop, { Crop } from 'react-image-crop';
import * as Yup from 'yup';

import { fileIsAnimatedPNG, fileToDataURLString } from '../../../models/filereader';
import { toYupValidationErrorString } from '../../../to-yup-validaiton-error';

const FILE_SIZE = 8_000_000; // approximately under 8MB
const SUPPORTED_FORMATS = ['image/jpg', 'image/jpeg', 'image/png'];

const isValidPNGOrJPG = async (file: File) => {
  if (file.type !== 'image/png') return true;
  const fileString = await fileToDataURLString(file);
  return !fileIsAnimatedPNG(fileString);
};

const fileSchema = Yup.mixed<File>()
  .required('A file is required')
  .test('fileSize', 'File too large', value => value?.size <= FILE_SIZE)
  .test('fileSize', 'File too small', value => value?.size > 1)
  .test('fileFormat', 'Unsupported format', value => SUPPORTED_FORMATS.includes(value?.type))
  .test('notAPNG', 'Multiple page PNG is not supported', file => isValidPNGOrJPG(file));

export const ImageUploader: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { values, setFieldValue, setFieldError, setFieldTouched } = useFormikContext<{
    customImageData: string;
    imageType: string;
  }>();

  const [isModalOpen, setModalOpen] = useState(false);

  const [uncroppedImageData, setUncroppedImageData] = useState<string>('');
  const [uncroppedImage, setUncroppedImage] = useState<HTMLImageElement | undefined>(undefined);
  const [crop, setCrop] = useState<Crop>({ unit: '%', x: 10, y: 10, width: 80, height: 80, aspect: 1 });

  const handleCustomImageDataChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.currentTarget.files && event.currentTarget.files.length > 0) {
      const file = event.currentTarget.files[0];
      event.currentTarget.value = '';

      try {
        await fileSchema.validate(file);

        const fileString = await fileToDataURLString(file);
        setUncroppedImageData(fileString);
        void setFieldValue('imageType', 'custom');
        setModalOpen(true);
      } catch (error) {
        setFieldError('customImageData', toYupValidationErrorString(error));
        void setFieldTouched('customImageData', true, false);
      }
    }
  };

  const onCropperImageLoaded = useCallback((image: HTMLImageElement) => {
    setUncroppedImage(image);

    const DEFAULT_CROP_RATIO = 0.8;
    const initialCropSize = min([image.width, image.height])! * DEFAULT_CROP_RATIO;

    setCrop({
      unit: 'px',
      x: (image.width - initialCropSize) / 2,
      y: (image.height - initialCropSize) / 2,
      width: initialCropSize,
      height: initialCropSize,
      aspect: 1,
    });
    return false;
  }, []);

  const generateCroppedDataString = (image: HTMLImageElement, selectedCrop: Crop) => {
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const ctx = canvas.getContext('2d');
    const pixelRatio = window.devicePixelRatio || 1;

    if (!ctx) {
      return null;
    }

    canvas.width = selectedCrop.width! * pixelRatio;
    canvas.height = selectedCrop.height! * pixelRatio;

    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(
      image,
      selectedCrop.x! * scaleX,
      selectedCrop.y! * scaleY,
      selectedCrop.width! * scaleX,
      selectedCrop.height! * scaleY,
      0,
      0,
      selectedCrop.width!,
      selectedCrop.height!
    );

    return canvas.toDataURL('image/png');
  };

  const submitCroppedImage = () => {
    if (uncroppedImage) {
      void setFieldValue('customImageData', generateCroppedDataString(uncroppedImage, crop));
      setModalOpen(false);
    }
  };

  return (
    <Box>
      <RadioGroup.ItemLabel
        id="goal-image-upload-label"
        htmlFor="customImageData"
        css={{
          p: 'space.0',
          m: 'space.0',
          backgroundColor: 'inherit',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          cursor: 'pointer',
        }}
      >
        {values.customImageData ? (
          <ThemedImage
            themedAsset={{ dark: values.customImageData, light: values.customImageData }}
            alt="Uploaded file"
          />
        ) : (
          <>
            <Icon name="Camera" size="small" />
            <Text variant="text25">Upload</Text>
          </>
        )}
        <input
          type="file"
          id="customImageData"
          name="customImageData"
          onChange={handleCustomImageDataChange}
          hidden={true}
          accept=".png, .jpg, .jpeg"
          aria-labelledby="goal-image-upload-label goal-image-selector"
        />
        <input
          type="text"
          id="addGoalCustomImageData"
          name="addGoalCustomImageData"
          hidden={true}
          aria-labelledby="goal-image-upload-label goal-image-selector"
          value={values.customImageData?.split(',')[1] || ''}
          readOnly={true}
        />
      </RadioGroup.ItemLabel>
      <VisuallyHidden>
        <RadioGroup.Item
          id="goal-image-custom"
          aria-labelledby="goal-image-custom-label goal-image-selector"
          value="custom"
        >
          <Text>custom</Text>
        </RadioGroup.Item>
      </VisuallyHidden>
      <Dialog open={isModalOpen} onOpenChange={setModalOpen}>
        <Dialog.Content css={{ display: 'grid', gap: 'space.300', p: 'space.400', overflowY: 'auto' }} size="large">
          <Dialog.Title asChild={true}>
            <H2>Crop your image</H2>
          </Dialog.Title>
          <VisuallyHidden asChild={true}>
            <Dialog.Description>Crop your uploaded image to a square</Dialog.Description>
          </VisuallyHidden>
          <Box>
            {uncroppedImageData && (
              <ReactCrop
                src={uncroppedImageData}
                onImageLoaded={onCropperImageLoaded}
                crop={crop}
                onChange={c => setCrop(c)}
                imageAlt="Crop selection"
              />
            )}
          </Box>
          <Box css={{ display: 'flex', gap: 'space.200', alignItems: 'center' }}>
            <Button onClick={submitCroppedImage} aria-label="Submit cropped image">
              Looks great
            </Button>
            <Dialog.Close asChild={true}>
              <Button variant="textSecondary">Cancel</Button>
            </Dialog.Close>
          </Box>
        </Dialog.Content>
      </Dialog>
    </Box>
  );
};
